import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Link2, Activity } from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

function PlayerProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [linkingPlayer, setLinkingPlayer] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/players/profile');
      setProfile(response.data);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Error al cargar perfil');
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await api.get('/stats/sync-status');
      setSyncStatus(response.data);
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  };

  useEffect(() => {
    if (user?.role === 'player') {
      fetchProfile();
      fetchSyncStatus();
    } else {
      setLoading(false);
    }
  }, [user?.role]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Prepare data with correct types
      const dataToSend = {
        ...formData,
        age: formData.age ? parseInt(formData.age, 10) : null
      };
      
      // Remove null/undefined values
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === null || dataToSend[key] === undefined || dataToSend[key] === '') {
          delete dataToSend[key];
        }
      });
      
      const response = await api.put('/players/profile', dataToSend);
      setProfile(response.data);
      setEditing(false);
      toast.success('Perfil actualizado');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error al actualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncStats = async () => {
    setSyncing(true);
    try {
      await api.post('/stats/sync', {});
      toast.success('Sincronización iniciada. Las estadísticas se actualizarán en breve.');
      setTimeout(() => {
        fetchProfile();
        fetchSyncStatus();
      }, 5000);
    } catch (error) {
      console.error('Error syncing stats:', error);
      toast.error('Error al sincronizar estadísticas');
    } finally {
      setSyncing(false);
    }
  };

  const handleLinkPlayer = async () => {
    const playerName = prompt('Introduce tu nombre completo como aparece en transfermarkt/sofascore:');
    if (!playerName) return;

    setLinkingPlayer(true);
    try {
      const response = await api.post(
        `/stats/link-player?player_name=${encodeURIComponent(playerName)}`,
        {}
      );
      toast.success(`¡Perfil vinculado exitosamente! ID: ${response.data.api_football_id}`);
      fetchProfile();
    } catch (error) {
      console.error('Error linking player:', error);
      toast.error(error.response?.data?.detail || 'Error al vincular jugador');
    } finally {
      setLinkingPlayer(false);
    }
  };

  const handleCancel = () => {
    setFormData(profile);
    setEditing(false);
  };

  if (user?.role !== 'player') {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto">
          <Card className="bento-card">
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">El perfil de jugador solo está disponible para jugadores.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="player-profile">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight mb-2">Mi Perfil</h1>
            <p className="text-muted-foreground">Gestiona tu información profesional</p>
          </div>
          <div className="flex gap-2">
            {profile?.api_football_id ? (
              <Button
                onClick={handleSyncStats}
                disabled={syncing}
                variant="outline"
                data-testid="sync-stats-button"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Sincronizando...' : 'Actualizar Stats'}
              </Button>
            ) : (
              <Button
                onClick={handleLinkPlayer}
                disabled={linkingPlayer}
                variant="outline"
              >
                <Link2 className="h-4 w-4 mr-2" />
                {linkingPlayer ? 'Vinculando...' : 'Vincular con API-Football'}
              </Button>
            )}
          </div>
        </div>

        {/* API Football Status */}
        {profile?.api_football_id && (
          <Card className="mb-6 border-green-200 bg-green-50">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-900">Perfil vinculado con API-Football</p>
                  <p className="text-sm text-green-700">
                    Tus estadísticas se actualizan automáticamente cada mes
                    {syncStatus?.last_sync && ` • Última actualización: ${new Date(syncStatus.last_sync).toLocaleDateString('es-ES')}`}
                  </p>
                </div>
              </div>
              <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                ID: {profile.api_football_id}
              </Badge>
            </CardContent>
          </Card>
        )}

        {/* Stats Overview */}
        {profile?.stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="bento-card">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-primary">{profile.stats.appearances || 0}</p>
                <p className="text-sm text-muted-foreground mt-1">Partidos</p>
              </CardContent>
            </Card>
            <Card className="bento-card">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{profile.stats.goals || 0}</p>
                <p className="text-sm text-muted-foreground mt-1">Goles</p>
              </CardContent>
            </Card>
            <Card className="bento-card">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{profile.stats.assists || 0}</p>
                <p className="text-sm text-muted-foreground mt-1">Asistencias</p>
              </CardContent>
            </Card>
            <Card className="bento-card">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-orange-600">
                  {profile.stats.rating ? parseFloat(profile.stats.rating).toFixed(1) : 'N/A'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">Rating</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Card className="bento-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Información Personal</CardTitle>
                <CardDescription>Actualiza tus datos y estadísticas</CardDescription>
              </div>
              {!editing ? (
                <Button onClick={() => setEditing(true)} data-testid="edit-profile-button">
                  Editar Perfil
                </Button>
              ) : (
                <div className="space-x-2">
                  <Button variant="outline" onClick={handleCancel} disabled={saving}>
                    Cancelar
                  </Button>
                  <Button onClick={handleSave} disabled={saving} data-testid="save-profile-button">
                    {saving ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Avatar */}
            <div className="flex items-center space-x-4">
              <Avatar className="h-24 w-24">
                <AvatarImage src={formData.profile_image || user?.picture} />
                <AvatarFallback className="text-2xl">{formData.full_name?.charAt(0)}</AvatarFallback>
              </Avatar>
              {editing && (
                <div className="flex-1">
                  <Label htmlFor="profile_image">URL de Imagen</Label>
                  <Input
                    id="profile_image"
                    value={formData.profile_image || ''}
                    onChange={(e) => setFormData({ ...formData, profile_image: e.target.value })}
                    placeholder="https://ejemplo.com/imagen.jpg"
                  />
                </div>
              )}
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="full_name">Nombre Completo</Label>
                <Input
                  id="full_name"
                  data-testid="profile-full-name-input"
                  value={formData.full_name || ''}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  disabled={!editing}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="position">Posición</Label>
                <Input
                  id="position"
                  data-testid="profile-position-input"
                  value={formData.position || ''}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  disabled={!editing}
                  placeholder="Ej: Delantero"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="age">Edad</Label>
                <Input
                  id="age"
                  type="number"
                  value={formData.age || ''}
                  onChange={(e) => setFormData({ ...formData, age: parseInt(e.target.value) || null })}
                  disabled={!editing}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="nationality">Nacionalidad</Label>
                <Input
                  id="nationality"
                  value={formData.nationality || ''}
                  onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                  disabled={!editing}
                  placeholder="Ej: España"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="current_club">Club Actual</Label>
                <Input
                  id="current_club"
                  data-testid="profile-club-input"
                  value={formData.current_club || ''}
                  onChange={(e) => setFormData({ ...formData, current_club: e.target.value })}
                  disabled={!editing}
                  placeholder="Ej: FC Barcelona"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="contract_end">Fin de Contrato</Label>
                <Input
                  id="contract_end"
                  type="date"
                  value={formData.contract_end || ''}
                  onChange={(e) => setFormData({ ...formData, contract_end: e.target.value })}
                  disabled={!editing}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="height">Altura</Label>
                <Input
                  id="height"
                  value={formData.height || ''}
                  onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                  disabled={!editing}
                  placeholder="Ej: 1.85m"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="weight">Peso</Label>
                <Input
                  id="weight"
                  value={formData.weight || ''}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  disabled={!editing}
                  placeholder="Ej: 80kg"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Bio */}
            <div>
              <Label htmlFor="bio">Biografía</Label>
              <Textarea
                id="bio"
                value={formData.bio || ''}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                disabled={!editing}
                placeholder="Escribe algo sobre ti..."
                rows={4}
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

export default PlayerProfile;
